import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  ConnectionState,
  DisconnectReason,
  LocalVideoTrack,
  Room,
  RoomEvent,
  Track,
  createLocalTracks,
  facingModeFromLocalTrack,
  type LocalTrack,
  type RemoteTrack,
  type RemoteTrackPublication,
  type RemoteParticipant,
  type LocalTrackPublication,
} from "livekit-client";
import {
  AppWindow,
  Mic,
  MicOff,
  Maximize2,
  Minimize2,
  PanelRight,
  PhoneOff,
  PictureInPicture2,
  ScreenShare,
  ScreenShareOff,
  SmilePlus,
  SwitchCamera,
  UserPlus,
  Video,
  VideoOff,
} from "lucide-react";

export type ChatCallSession = {
  callId: string;
  conversationId: number;
  roomName: string;
  livekitUrl: string;
  token: string;
  media: "audio" | "video";
  role: "caller" | "callee";
  peerLabel: string;
};

/** dock = 3e colonne ; panel = flottante ; immersive = plein écran ; pip = pastille mobile */
export type ChatCallChrome = "dock" | "panel" | "immersive" | "pip";

type InviteCandidate = {
  id: number;
  label: string;
  presence?: string;
  avatarUrl?: string | null;
  clients?: string[];
  inGroup?: boolean;
};

type Props = {
  session: ChatCallSession;
  onHangup: () => void;
  /** Appelant : quelqu’un a rejoint (stop sonnerie sortante). */
  onRemoteJoined?: () => void;
  /** Connexion LiveKit locale OK (salle ouverte) — stop timer « pas de réponse ». */
  onLocalConnected?: () => void;
  /** Même compte a repris l’appel ailleurs (bascule PC → mobile) — ne pas raccrocher serveur. */
  onSuperseded?: () => void;
  chrome: ChatCallChrome;
  onChromeChange: (chrome: ChatCallChrome) => void;
  canInviteOthers?: boolean;
  inviteCandidates?: InviteCandidate[];
  onInviteParticipant?: (userId: number) => Promise<void>;
  onInviteGuest?: (email: string) => Promise<string | null>;
  onCopyGuestLink?: () => Promise<string | null>;
  /** Salle / invité : pas de libellé « Sonnerie » en attendant le remote. */
  awaitingGuest?: boolean;
};

type Facing = "user" | "environment";

type FloatingEmoji = {
  id: string;
  emoji: string;
  leftPct: number;
  driftPx: number;
  delayMs: number;
  scale: number;
  durationMs: number;
};

const CALL_EMOJI_TOPIC = "aurelia-call-emoji";
const CALL_REACTION_EMOJIS = ["❤️", "😂", "👍", "🔥", "👏", "🎉", "😍", "😮"] as const;

function formatCallElapsed(totalSec: number): string {
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function friendlyCallError(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e ?? "");
  if (/Permission|NotAllowed|denied/i.test(msg)) {
    return "Autorise le micro / la caméra dans le navigateur.";
  }
  if (/NotFound|DevicesNotFound/i.test(msg)) {
    return "Aucun micro ou caméra détecté.";
  }
  if (/publisher|pcManager|PeerConnection|ICE|timeout/i.test(msg)) {
    return `Média WebRTC KO (${msg}). Vérifie firewall OVH : TCP 7881 + UDP 3478.`;
  }
  return msg || "Connexion visio impossible";
}

async function listVideoInputs(): Promise<MediaDeviceInfo[]> {
  try {
    return await Room.getLocalDevices("videoinput", true);
  } catch {
    try {
      if (!navigator.mediaDevices?.enumerateDevices) return [];
      const list = await navigator.mediaDevices.enumerateDevices();
      return list.filter((d) => d.kind === "videoinput");
    } catch {
      return [];
    }
  }
}

/** Visio : camera + micro ; si pas de camera, on garde le micro. */
async function createCallLocalTracks(wantVideo: boolean): Promise<{
  tracks: LocalTrack[];
  videoOk: boolean;
}> {
  if (wantVideo) {
    try {
      const tracks = await createLocalTracks({
        audio: true,
        video: { facingMode: "user" },
      });
      return {
        tracks,
        videoOk: tracks.some((t) => t.kind === Track.Kind.Video),
      };
    } catch (e) {
      console.warn("[chat-call] camera indisponible, repli micro", e);
    }
  }
  const tracks = await createLocalTracks({ audio: true, video: false });
  return { tracks, videoOk: false };
}

function guessFacingFromLabel(label: string): Facing | null {
  const l = label.toLowerCase();
  if (/back|rear|environment|arrière|arriere|world|posterior/i.test(l)) return "environment";
  if (/front|user|face|avant|selfie/i.test(l)) return "user";
  return null;
}

export function ChatCallOverlay({
  session,
  onHangup,
  onRemoteJoined,
  onLocalConnected,
  onSuperseded,
  chrome,
  onChromeChange,
  canInviteOthers = false,
  inviteCandidates = [],
  onInviteParticipant,
  onInviteGuest,
  onCopyGuestLink,
  awaitingGuest = false,
}: Props) {
  const roomRef = useRef<Room | null>(null);
  const localTracksRef = useRef<LocalTrack[]>([]);
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const localScreenVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const sharePipWinRef = useRef<Window | null>(null);
  const shellRef = useRef<HTMLDivElement | null>(null);
  const panelDragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    origLeft: number;
    origTop: number;
  } | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const facingRef = useRef<Facing>("user");
  const onRemoteJoinedRef = useRef(onRemoteJoined);
  const onLocalConnectedRef = useRef(onLocalConnected);
  const onSupersededRef = useRef(onSuperseded);
  const remoteJoinedNotifiedRef = useRef(false);
  const [status, setStatus] = useState<"connecting" | "connected" | "error">("connecting");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(session.media === "video");
  const [remoteReady, setRemoteReady] = useState(false);
  const [facing, setFacing] = useState<Facing>("user");
  const [canFlipCam, setCanFlipCam] = useState(false);
  const [flipBusy, setFlipBusy] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [remoteScreen, setRemoteScreen] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const remoteScreenActiveRef = useRef(false);
  const remoteCameraTrackRef = useRef<RemoteTrack | null>(null);
  /** true = moi en grand, correspondant en pastille */
  const [selfPrimary, setSelfPrimary] = useState(false);
  /** Position libre du panneau flottant (null = ancré bas-droite). */
  const [panelPos, setPanelPos] = useState<{ left: number; top: number } | null>(null);
  const [floatingEmojis, setFloatingEmojis] = useState<FloatingEmoji[]>([]);
  const [showReactions, setShowReactions] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [guestEmail, setGuestEmail] = useState("");
  const [guestHint, setGuestHint] = useState<string | null>(null);
  const canOpenInvite = Boolean((canInviteOthers && onInviteParticipant) || onInviteGuest);
  const [remoteCams, setRemoteCams] = useState<{ identity: string; name: string }[]>([]);
  const remoteCamElsRef = useRef<Map<string, HTMLVideoElement>>(new Map());
  const remoteCamTracksRef = useRef<Map<string, RemoteTrack>>(new Map());
  const spawnEmojiRef = useRef<(emoji: string) => void>(() => undefined);

  const setChrome = onChromeChange;

  onRemoteJoinedRef.current = onRemoteJoined;
  onLocalConnectedRef.current = onLocalConnected;
  onSupersededRef.current = onSuperseded;

  const spawnEmoji = useCallback((emoji: string) => {
    if (!emoji) return;
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }
    const burst = 10 + Math.floor(Math.random() * 5); // 10–14 comme une pluie
    const stamp = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const items: FloatingEmoji[] = Array.from({ length: burst }, (_, i) => ({
      id: `${stamp}-${i}`,
      emoji,
      leftPct: 10 + Math.random() * 80,
      driftPx: -48 + Math.random() * 96,
      delayMs: Math.floor(Math.random() * 420),
      scale: 0.75 + Math.random() * 0.7,
      durationMs: 2200 + Math.floor(Math.random() * 900),
    }));
    setFloatingEmojis((prev) => [...prev.slice(-40), ...items]);
    const maxLife = Math.max(...items.map((it) => it.delayMs + it.durationMs)) + 80;
    window.setTimeout(() => {
      setFloatingEmojis((prev) => prev.filter((e) => !e.id.startsWith(stamp)));
    }, maxLife);
  }, []);

  spawnEmojiRef.current = spawnEmoji;

  useEffect(() => {
    if (!remoteReady || status !== "connected") return;
    setElapsedSec(0);
    const id = window.setInterval(() => {
      setElapsedSec((s) => s + 1);
    }, 1000);
    return () => window.clearInterval(id);
  }, [remoteReady, status]);

  // Partage distant → écran en grand (pattern Teams / Zoom)
  useEffect(() => {
    if (remoteScreen) setSelfPrimary(false);
  }, [remoteScreen]);

  const closeSharePip = useCallback(() => {
    try {
      sharePipWinRef.current?.close();
    } catch {
      /* ignore */
    }
    sharePipWinRef.current = null;
  }, []);

  /** Aperçu du partage dans une fenêtre flottante (Document PiP). */
  const popOutSharePreview = useCallback(async () => {
    const room = roomRef.current;
    const pub = room?.localParticipant.getTrackPublication(Track.Source.ScreenShare);
    const track = pub?.track;
    if (!(track instanceof LocalVideoTrack)) return;
    const mst = track.mediaStreamTrack;
    if (!mst) return;

    const dpip = (
      window as Window & {
        documentPictureInPicture?: {
          requestWindow: (opts?: { width?: number; height?: number }) => Promise<Window>;
        };
      }
    ).documentPictureInPicture;
    if (!dpip?.requestWindow) return;

    closeSharePip();
    try {
      const win = await dpip.requestWindow({ width: 400, height: 260 });
      sharePipWinRef.current = win;
      const doc = win.document;
      doc.head.innerHTML = `<style>
        html,body{margin:0;height:100%;background:#052e16;overflow:hidden}
        .wrap{box-sizing:border-box;height:100%;padding:6px;border:4px solid #22c55e;background:#052e16}
        video{width:100%;height:calc(100% - 22px);object-fit:contain;background:#000;display:block;border-radius:6px}
        .lbl{height:22px;display:flex;align-items:center;justify-content:center;color:#bbf7d0;font:600 11px/1 system-ui,sans-serif}
      </style>`;
      doc.body.innerHTML = `<div class="wrap"><video autoplay playsinline muted disablePictureInPicture></video><div class="lbl">Aperçu — vous partagez</div></div>`;
      const video = doc.querySelector("video");
      if (video) {
        video.disablePictureInPicture = true;
        video.srcObject = new MediaStream([mst]);
        void video.play().catch(() => undefined);
      }
      win.addEventListener("pagehide", () => {
        if (sharePipWinRef.current === win) sharePipWinRef.current = null;
      });
    } catch (err) {
      console.warn("[chat-call] share pip", err);
    }
  }, [closeSharePip]);

  useEffect(() => {
    if (!sharing) {
      closeSharePip();
      return;
    }
    const room = roomRef.current;
    const pub = room?.localParticipant.getTrackPublication(Track.Source.ScreenShare);
    const track = pub?.track;
    if (track instanceof LocalVideoTrack && localScreenVideoRef.current) {
      track.attach(localScreenVideoRef.current);
    }
  }, [sharing, closeSharePip]);

  const sendReaction = useCallback(
    (emoji: string) => {
      spawnEmoji(emoji);
      const room = roomRef.current;
      if (!room || room.state !== ConnectionState.Connected) return;
      try {
        const payload = new TextEncoder().encode(JSON.stringify({ t: "rx", e: emoji }));
        void room.localParticipant.publishData(payload, {
          reliable: false,
          topic: CALL_EMOJI_TOPIC,
        });
      } catch (err) {
        console.warn("[chat-call] reaction publish", err);
      }
    },
    [spawnEmoji]
  );

  // Connexion LiveKit UNE fois par session — ne pas dépendre des callbacks parent
  // (sinon chaque setState App → reconnect → boucle « coupe / reprend »).
  useEffect(() => {
    let cancelled = false;
    remoteJoinedNotifiedRef.current = false;
    const room = new Room({
      adaptiveStream: true,
      dynacast: true,
    });
    roomRef.current = room;

    const attachRemoteVideo = (
      track: RemoteTrack,
      source: Track.Source,
      participant: RemoteParticipant
    ) => {
      if (source === Track.Source.ScreenShare) {
        if (!remoteVideoRef.current) return;
        remoteScreenActiveRef.current = true;
        track.attach(remoteVideoRef.current);
        setRemoteScreen(true);
        setRemoteReady(true);
        if (!remoteJoinedNotifiedRef.current) {
          remoteJoinedNotifiedRef.current = true;
          onRemoteJoinedRef.current?.();
        }
        return;
      }
      if (source === Track.Source.Camera) {
        const identity = participant.identity;
        remoteCameraTrackRef.current = track;
        remoteCamTracksRef.current.set(identity, track);
        setRemoteCams((prev) =>
          prev.some((p) => p.identity === identity)
            ? prev
            : [
                ...prev,
                {
                  identity,
                  name: (participant.name?.trim() || identity).replace(/ \(invité\)$/u, " · invité"),
                },
              ]
        );
        const tile = remoteCamElsRef.current.get(identity);
        if (tile) track.attach(tile);
        if (!remoteScreenActiveRef.current && remoteVideoRef.current) {
          track.attach(remoteVideoRef.current);
          setRemoteScreen(false);
        }
        setRemoteReady(true);
        if (!remoteJoinedNotifiedRef.current) {
          remoteJoinedNotifiedRef.current = true;
          onRemoteJoinedRef.current?.();
        }
      }
    };

    const onTrackSubscribed = (
      track: RemoteTrack,
      pub: RemoteTrackPublication,
      participant: RemoteParticipant
    ) => {
      if (track.kind === Track.Kind.Video) {
        attachRemoteVideo(track, pub.source, participant);
      }
      if (track.kind === Track.Kind.Audio && remoteAudioRef.current) {
        track.attach(remoteAudioRef.current);
        setRemoteReady(true);
        if (!remoteJoinedNotifiedRef.current) {
          remoteJoinedNotifiedRef.current = true;
          onRemoteJoinedRef.current?.();
        }
      }
    };

    const onTrackUnsubscribed = (
      track: RemoteTrack,
      pub: RemoteTrackPublication,
      participant: RemoteParticipant
    ) => {
      if (pub.source === Track.Source.ScreenShare) {
        remoteScreenActiveRef.current = false;
        setRemoteScreen(false);
        try {
          track.detach();
        } catch {
          /* ignore */
        }
        const cam = remoteCameraTrackRef.current;
        if (cam && remoteVideoRef.current) {
          cam.attach(remoteVideoRef.current);
        }
      }
      if (pub.source === Track.Source.Camera) {
        const identity = participant.identity;
        if (remoteCameraTrackRef.current === track) remoteCameraTrackRef.current = null;
        remoteCamTracksRef.current.delete(identity);
        setRemoteCams((prev) => prev.filter((p) => p.identity !== identity));
      }
    };

    const onLocalTrack = (pub: LocalTrackPublication) => {
      if (pub.source === Track.Source.ScreenShare) {
        setSharing(true);
        return;
      }
      if (pub.track?.kind === Track.Kind.Video && localVideoRef.current) {
        pub.track.attach(localVideoRef.current);
      }
    };

    const onLocalTrackUnpublished = (pub: LocalTrackPublication) => {
      if (pub.source === Track.Source.ScreenShare) {
        setSharing(false);
        try {
          pub.track?.detach();
        } catch {
          /* ignore */
        }
      }
    };

    room.on(RoomEvent.TrackSubscribed, onTrackSubscribed);
    room.on(RoomEvent.TrackUnsubscribed, onTrackUnsubscribed);
    room.on(RoomEvent.LocalTrackPublished, onLocalTrack);
    room.on(RoomEvent.LocalTrackUnpublished, onLocalTrackUnpublished);
    room.on(RoomEvent.Disconnected, (reason) => {
      if (cancelled) return;
      if (reason === DisconnectReason.DUPLICATE_IDENTITY) {
        onSupersededRef.current?.();
      }
    });
    room.on(RoomEvent.DataReceived, (payload, _participant, _kind, topic) => {
      if (topic && topic !== CALL_EMOJI_TOPIC) return;
      try {
        const raw = new TextDecoder().decode(payload);
        const msg = JSON.parse(raw) as { t?: string; e?: string };
        if (msg?.t === "rx" && typeof msg.e === "string" && msg.e.length <= 8) {
          spawnEmojiRef.current(msg.e);
        }
      } catch {
        /* ignore */
      }
    });

    const livekitUrl = session.livekitUrl;
    const token = session.token;
    const media = session.media;

    (async () => {
      try {
        const wantVideo = media === "video";
        const acquired = await createCallLocalTracks(wantVideo);
        const localTracks = acquired.tracks;
        if (cancelled) {
          localTracks.forEach((t) => t.stop());
          return;
        }
        localTracksRef.current = localTracks;
        if (wantVideo && !acquired.videoOk) {
          setCamOn(false);
          setErrorMsg("Pas de caméra — vous êtes en visio (micro).");
        }

        const videoTrack = localTracks.find((t) => t.kind === Track.Kind.Video);
        if (videoTrack instanceof LocalVideoTrack) {
          const detected = facingModeFromLocalTrack(videoTrack).facingMode;
          const nextFacing: Facing = detected === "environment" ? "environment" : "user";
          facingRef.current = nextFacing;
          setFacing(nextFacing);
          if (localVideoRef.current) videoTrack.attach(localVideoRef.current);
        }

        if (wantVideo) {
          const devices = await listVideoInputs();
          if (!cancelled) setCanFlipCam(devices.length > 1);
        }

        await room.connect(livekitUrl, token, {
          autoSubscribe: true,
          peerConnectionTimeout: 45_000,
        });
        if (cancelled) return;

        if (room.state !== ConnectionState.Connected) {
          throw new Error(`État room inattendu: ${room.state}`);
        }

        // Laisse le PC publisher s’établir (évite race pcManager.publisher)
        await new Promise((r) => setTimeout(r, 400));
        if (cancelled) return;

        for (const track of localTracks) {
          if (cancelled) return;
          if (room.state !== ConnectionState.Connected) {
            throw new Error("Room déconnectée avant publish");
          }
          await room.localParticipant.publishTrack(track);
        }
        if (cancelled) return;

        room.remoteParticipants.forEach((p) => {
          p.trackPublications.forEach((pub) => {
            if (!pub.track) return;
            const track = pub.track;
            if (track.kind === Track.Kind.Video) {
              attachRemoteVideo(track, pub.source, p);
            }
            if (track.kind === Track.Kind.Audio && remoteAudioRef.current) {
              track.attach(remoteAudioRef.current);
              setRemoteReady(true);
              if (!remoteJoinedNotifiedRef.current) {
                remoteJoinedNotifiedRef.current = true;
                onRemoteJoinedRef.current?.();
              }
            }
          });
        });

        setStatus("connected");
        onLocalConnectedRef.current?.();
      } catch (e) {
        console.error("[chat-call]", e);
        if (cancelled) return;
        setStatus("error");
        setErrorMsg(friendlyCallError(e));
        localTracksRef.current.forEach((t) => t.stop());
        localTracksRef.current = [];
        try {
          await room.disconnect();
        } catch {
          /* ignore */
        }
      }
    })();

    return () => {
      cancelled = true;
      try {
        sharePipWinRef.current?.close();
      } catch {
        /* ignore */
      }
      sharePipWinRef.current = null;
      room.removeAllListeners();
      localTracksRef.current.forEach((t) => {
        try {
          t.stop();
        } catch {
          /* ignore */
        }
      });
      localTracksRef.current = [];
      void room.disconnect().catch(() => undefined);
      roomRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reconnect seulement si URL/token/média changent
  }, [session.callId, session.livekitUrl, session.token, session.media]);

  const toggleMic = async () => {
    const room = roomRef.current;
    if (!room || room.state !== ConnectionState.Connected) return;
    try {
      const next = !micOn;
      await room.localParticipant.setMicrophoneEnabled(next);
      setMicOn(next);
    } catch (e) {
      setErrorMsg(friendlyCallError(e));
    }
  };

  const toggleCam = async () => {
    const room = roomRef.current;
    if (!room || room.state !== ConnectionState.Connected) return;
    try {
      const next = !camOn;
      await room.localParticipant.setCameraEnabled(next);
      setCamOn(next);
    } catch (e) {
      setErrorMsg(friendlyCallError(e));
    }
  };

  const toggleScreenShare = async () => {
    const room = roomRef.current;
    if (!room || room.state !== ConnectionState.Connected) return;
    try {
      const next = !sharing;
      await room.localParticipant.setScreenShareEnabled(next, {
        audio: true,
        selfBrowserSurface: "include",
      });
      setSharing(next);
      // Même geste utilisateur que le clic « partager » → ouvre l’aperçu flottant par défaut
      if (next) {
        await popOutSharePreview();
      } else {
        closeSharePip();
      }
    } catch (e) {
      console.warn("[chat-call] screen share", e);
      const msg = e instanceof Error ? e.message : String(e ?? "");
      if (/Permission|NotAllowed|denied|cancel/i.test(msg)) {
        setErrorMsg("Partage d’écran annulé ou refusé.");
      } else {
        setErrorMsg("Impossible de partager l’écran.");
      }
      setSharing(false);
      closeSharePip();
    }
  };

  const flipCamera = async () => {
    if (flipBusy || !camOn || session.media !== "video") return;
    const room = roomRef.current;
    if (!room || room.state !== ConnectionState.Connected) return;

    const pub = room.localParticipant.getTrackPublication(Track.Source.Camera);
    const track = pub?.track;
    if (!(track instanceof LocalVideoTrack)) return;

    setFlipBusy(true);
    try {
      const devices = await listVideoInputs();
      setCanFlipCam(devices.length > 1);

      const currentId = track.mediaStreamTrack.getSettings().deviceId ?? "";
      const currentIdx = devices.findIndex((d) => d.deviceId === currentId);
      const wantFacing: Facing = facingRef.current === "user" ? "environment" : "user";

      let nextDevice: MediaDeviceInfo | undefined;
      if (devices.length >= 2) {
        // Préfère une caméra dont le label correspond à avant/arrière
        nextDevice = devices.find((d, i) => {
          if (i === currentIdx) return false;
          return guessFacingFromLabel(d.label) === wantFacing;
        });
        // Sinon : autre device dans la liste (cycle)
        if (!nextDevice) {
          nextDevice = devices[(Math.max(currentIdx, 0) + 1) % devices.length];
          if (nextDevice?.deviceId === currentId && devices.length > 1) {
            nextDevice = devices.find((d) => d.deviceId !== currentId);
          }
        }
      }

      if (nextDevice?.deviceId && nextDevice.deviceId !== currentId) {
        // Vrai changement de périphérique (pas un miroir CSS)
        await room.switchActiveDevice("videoinput", nextDevice.deviceId, true);
      } else {
        // Fallback navigateur : contrainte facingMode exacte
        await track.restartTrack({
          facingMode: wantFacing,
        });
      }

      // Re-attache preview + met à jour facing réel
      const livePub = room.localParticipant.getTrackPublication(Track.Source.Camera);
      const liveTrack = livePub?.track;
      if (liveTrack instanceof LocalVideoTrack && localVideoRef.current) {
        liveTrack.attach(localVideoRef.current);
        const detected = facingModeFromLocalTrack(liveTrack).facingMode;
        const nextFacing: Facing =
          detected === "environment"
            ? "environment"
            : guessFacingFromLabel(liveTrack.mediaStreamTrack.label) === "environment"
              ? "environment"
              : detected === "user"
                ? "user"
                : wantFacing;
        facingRef.current = nextFacing;
        setFacing(nextFacing);
      } else if (localVideoRef.current) {
        track.attach(localVideoRef.current);
        facingRef.current = wantFacing;
        setFacing(wantFacing);
      }
    } catch (e) {
      console.warn("[chat-call] flip camera", e);
      setErrorMsg("Impossible de changer de caméra.");
    } finally {
      setFlipBusy(false);
    }
  };

  const hangup = () => {
    localTracksRef.current.forEach((t) => t.stop());
    localTracksRef.current = [];
    void roomRef.current?.disconnect().catch(() => undefined);
    onHangup();
  };

  const isVideo = session.media === "video";
  const isPip = isVideo && chrome === "pip";
  const isImmersive = !isVideo || chrome === "immersive";
  const isPanel = isVideo && chrome === "panel";
  const isDock = isVideo && chrome === "dock";

  useEffect(() => {
    if (!isPanel) setPanelPos(null);
  }, [isPanel]);

  // Dock desktop only — sinon fallback plein écran
  useEffect(() => {
    if (chrome !== "dock") return;
    const mq = window.matchMedia("(min-width: 768px)");
    const sync = () => {
      if (!mq.matches) onChromeChange("immersive");
    };
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, [chrome, onChromeChange]);

  const clampPanelPos = useCallback((left: number, top: number, el: HTMLElement) => {
    const pad = 8;
    const maxL = Math.max(pad, window.innerWidth - el.offsetWidth - pad);
    const maxT = Math.max(pad, window.innerHeight - el.offsetHeight - pad);
    return {
      left: Math.min(Math.max(pad, left), maxL),
      top: Math.min(Math.max(pad, top), maxT),
    };
  }, []);

  const onPanelDragStart = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!isPanel || e.button !== 0) return;
      if ((e.target as HTMLElement | null)?.closest?.("button")) return;
      const el = shellRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const left = panelPos?.left ?? rect.left;
      const top = panelPos?.top ?? rect.top;
      setPanelPos({ left, top });
      panelDragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        origLeft: left,
        origTop: top,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [isPanel, panelPos]
  );

  const onPanelDragMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const drag = panelDragRef.current;
      const el = shellRef.current;
      if (!drag || !el || drag.pointerId !== e.pointerId) return;
      setPanelPos(
        clampPanelPos(
          drag.origLeft + (e.clientX - drag.startX),
          drag.origTop + (e.clientY - drag.startY),
          el
        )
      );
    },
    [clampPanelPos]
  );

  const onPanelDragEnd = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (panelDragRef.current?.pointerId === e.pointerId) {
      panelDragRef.current = null;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    }
  }, []);

  const statusLine =
    status === "connecting"
      ? "Connexion…"
      : status === "error"
        ? errorMsg || "Erreur"
        : remoteReady
          ? `${formatCallElapsed(elapsedSec)}${remoteScreen ? " · Partage distant" : ""}${
              sharing ? " · Tu partages" : ""
            }`
          : session.role === "caller"
            ? awaitingGuest
              ? "En attente de l’invité…"
              : "Sonnerie…"
            : "En attente du correspondant…";

  const statusChipMod =
    status === "error"
      ? "chat-call-status-chip--error"
      : remoteReady
        ? ""
        : "chat-call-status-chip--ringing";

  const shellClass = isVideo
    ? [
        "flex flex-col overflow-hidden bg-[#070b14] text-white transition-[width,height,border-radius] duration-200 ease-out",
        isDock
          ? "chat-call-shell--dock fixed z-[70] hidden md:flex"
          : isPip
            ? "fixed z-[80] top-14 right-3 h-[248px] w-[164px] rounded-2xl border border-white/15 shadow-[0_16px_48px_rgba(0,0,0,0.5)]"
            : isPanel
              ? `fixed z-[80] chat-call-shell--panel h-[min(760px,calc(100dvh-3rem))] w-[min(680px,calc(100vw-2.5rem))] max-h-[calc(100dvh-3rem)] rounded-[1.75rem] border border-white/12${
                  panelPos ? " chat-call-shell--dragged" : ""
                }`
              : "fixed z-[80] inset-0",
      ].join(" ")
    : "fixed inset-x-0 bottom-0 z-[80] flex flex-col overflow-hidden bg-[#070b14]/95 text-white backdrop-blur-md md:inset-0 md:m-auto md:h-auto md:max-h-[min(420px,calc(100dvh-3rem))] md:w-[min(400px,calc(100vw-2.5rem))] md:rounded-[1.75rem] md:border md:border-white/12 md:shadow-[0_28px_80px_rgba(0,0,0,0.55)]";

  const panelStyle: CSSProperties | undefined =
    isPanel && panelPos
      ? { left: panelPos.left, top: panelPos.top, right: "auto", bottom: "auto" }
      : undefined;

  const showCallBackdrop = !isVideo; // audio desktop seulement — panneau visio laisse le chat libre

  // Plein écran / partage : contain (pas de crop zoom). Panneau flottant : cover un peu plus « rempli ».
  const mainFitCls =
    remoteScreen || isImmersive || selfPrimary
      ? "object-contain bg-black"
      : "object-cover";
  const mainVideoCls = `absolute inset-0 h-full w-full ${mainFitCls}`;
  const thumbVideoCls =
    "absolute z-[5] chat-call-thumb object-cover cursor-pointer " +
    (isPip
      ? "bottom-1.5 right-1.5 h-16 w-12"
      : "bottom-3 right-3 h-28 w-20 md:bottom-4 md:right-4 md:h-36 md:w-28");

  const toggleStage = () => {
    if (isPip) return;
    setSelfPrimary((v) => !v);
  };

  const shell = (
    <div
      ref={shellRef}
      className={`chat-call-overlay ${shellClass}`}
      style={panelStyle}
      role="dialog"
      aria-label={`Appel avec ${session.peerLabel}`}
    >
      <div className="pointer-events-none absolute inset-0 z-[15] overflow-hidden" aria-hidden>
        {floatingEmojis.map((item) => (
          <span
            key={item.id}
            className={`chat-call-emoji-float ${isPip ? "chat-call-emoji-float--pip" : ""}`}
            style={
              {
                left: `${item.leftPct}%`,
                animationDelay: `${item.delayMs}ms`,
                animationDuration: `${item.durationMs}ms`,
                ["--chat-emoji-drift" as string]: `${item.driftPx}px`,
                ["--chat-emoji-scale" as string]: String(item.scale),
              } as CSSProperties
            }
          >
            {item.emoji}
          </span>
        ))}
      </div>
      {!isPip ? (
        <div
          className={`chat-call-header relative z-10 flex items-center justify-between gap-3 px-4 pb-5 pt-3.5 ${
            isPanel ? "chat-call-header--drag" : ""
          }`}
          onPointerDown={isPanel ? onPanelDragStart : undefined}
          onPointerMove={isPanel ? onPanelDragMove : undefined}
          onPointerUp={isPanel ? onPanelDragEnd : undefined}
          onPointerCancel={isPanel ? onPanelDragEnd : undefined}
          title={isPanel ? "Glisser pour déplacer la fenêtre" : undefined}
        >
          <div className="min-w-0">
            <p className="truncate text-[15px] font-semibold tracking-tight">{session.peerLabel}</p>
            <div className={`chat-call-status-chip mt-1.5 ${statusChipMod}`}>{statusLine}</div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {isVideo ? (
              <div
                className="chat-call-mode-switch mr-0.5 hidden md:flex"
                role="group"
                aria-label="Mode d’affichage"
              >
                <button
                  type="button"
                  className={`chat-call-mode-btn ${isDock ? "is-active" : ""}`}
                  onClick={() => setChrome("dock")}
                  title="Ancré à droite"
                  aria-label="Ancré à droite"
                  aria-pressed={isDock}
                >
                  <PanelRight className="size-4" />
                </button>
                <button
                  type="button"
                  className={`chat-call-mode-btn ${isPanel ? "is-active" : ""}`}
                  onClick={() => setChrome("panel")}
                  title="Fenêtre flottante"
                  aria-label="Fenêtre flottante"
                  aria-pressed={isPanel}
                >
                  <AppWindow className="size-4" />
                </button>
                <button
                  type="button"
                  className={`chat-call-mode-btn ${chrome === "immersive" ? "is-active" : ""}`}
                  onClick={() => setChrome("immersive")}
                  title="Plein écran"
                  aria-label="Plein écran"
                  aria-pressed={chrome === "immersive"}
                >
                  <Maximize2 className="size-4" />
                </button>
              </div>
            ) : null}
            {isVideo && isImmersive ? (
              <button
                type="button"
                onClick={() =>
                  setChrome(
                    typeof window !== "undefined" &&
                      window.matchMedia("(min-width: 768px)").matches
                      ? "dock"
                      : "pip"
                  )
                }
                className="chat-call-icon-btn flex"
                aria-label={
                  typeof window !== "undefined" &&
                  window.matchMedia("(min-width: 768px)").matches
                    ? "Quitter le plein écran"
                    : "Réduire en pastille flottante"
                }
                title="Réduire"
              >
                <PictureInPicture2 className="size-5 md:hidden" />
                <Minimize2 className="hidden size-5 md:block" />
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {isVideo ? (
        <div
          className={`relative min-h-0 flex-1 bg-[#0a101c] ${isPip ? "cursor-pointer" : ""}`}
          onClick={
            isPip
              ? () => {
                  setChrome("immersive");
                }
              : undefined
          }
          onKeyDown={
            isPip
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setChrome("immersive");
                  }
                }
              : undefined
          }
          role={isPip ? "button" : undefined}
          tabIndex={isPip ? 0 : undefined}
          aria-label={isPip ? "Agrandir l’appel en plein écran" : undefined}
        >
          {/* Distant */}
          <video
            ref={remoteVideoRef}
            className={`${selfPrimary ? thumbVideoCls : mainVideoCls} ${
              remoteReady ? "" : selfPrimary ? "opacity-0" : "opacity-0"
            }`}
            playsInline
            autoPlay
            disablePictureInPicture
            controlsList="nodownload nofullscreen noremoteplayback"
            disableRemotePlayback
            onClick={
              selfPrimary && !isPip
                ? (e) => {
                    e.stopPropagation();
                    toggleStage();
                  }
                : undefined
            }
            title={selfPrimary && !isPip ? "Afficher le correspondant en grand" : undefined}
          />
          {/* Local */}
          <video
            ref={localVideoRef}
            className={`${selfPrimary ? mainVideoCls : thumbVideoCls} ${
              facing === "user" ? "-scale-x-100" : ""
            } ${camOn ? "" : "opacity-0"}`}
            playsInline
            autoPlay
            muted
            disablePictureInPicture
            controlsList="nodownload nofullscreen noremoteplayback"
            disableRemotePlayback
            onClick={
              !selfPrimary && !isPip
                ? (e) => {
                    e.stopPropagation();
                    toggleStage();
                  }
                : undefined
            }
            title={!selfPrimary && !isPip ? "M’afficher en grand" : undefined}
          />
          {!remoteReady && !selfPrimary ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className={`text-center ${isPip ? "px-2" : "px-6"}`}>
                <div
                  className={`mx-auto mb-2 flex items-center justify-center rounded-full bg-white/10 font-semibold ${
                    isPip ? "size-12 text-base" : "mb-3 size-20 text-2xl"
                  }`}
                >
                  {session.peerLabel.slice(0, 1).toUpperCase()}
                </div>
                {!isPip ? (
                  <p className="text-sm text-white/70">
                    {status === "error"
                      ? errorMsg
                      : session.role === "caller"
                        ? awaitingGuest
                          ? "En attente de l’invité…"
                          : "Sonnerie…"
                        : "Appel en cours…"}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* Bandeau partage */}
          {(sharing || remoteScreen) && !isPip ? (
            <div className="absolute inset-x-0 top-3 z-[12] flex justify-center px-3">
              {sharing ? (
                <div className="chat-call-share-banner" role="status">
                  <span className="chat-call-share-dot" aria-hidden />
                  <ScreenShare className="size-4 shrink-0" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">Vous partagez votre écran</span>
                  <button
                    type="button"
                    className="shrink-0 rounded-lg bg-white/20 px-3 py-1.5 text-xs font-semibold hover:bg-white/30"
                    onClick={(e) => {
                      e.stopPropagation();
                      void toggleScreenShare();
                    }}
                  >
                    Arrêter le partage
                  </button>
                </div>
              ) : null}
              {!sharing && remoteScreen ? (
                <div className="chat-call-share-banner chat-call-share-banner--remote" role="status">
                  <span className="chat-call-share-dot" aria-hidden />
                  <ScreenShare className="size-4 shrink-0" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">
                    {session.peerLabel} partage son écran
                  </span>
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Miniature interne (aperçu flottant ouvert auto au démarrage du partage) */}
          {sharing && !isPip ? (
            <div className="absolute bottom-3 left-3 z-[6] overflow-hidden rounded-xl border-[3px] border-emerald-400 shadow-[0_0_0_2px_rgba(16,185,129,0.35)]">
              <video
                ref={localScreenVideoRef}
                className="h-[4.75rem] w-32 bg-black object-contain md:h-24 md:w-40"
                playsInline
                autoPlay
                muted
                disablePictureInPicture
                controlsList="nodownload nofullscreen noremoteplayback"
                disableRemotePlayback
              />
              <span className="absolute inset-x-0 bottom-0 bg-emerald-600/95 px-1.5 py-0.5 text-center text-[9px] font-semibold uppercase tracking-wide text-white">
                Votre partage
              </span>
            </div>
          ) : (
            <video
              ref={localScreenVideoRef}
              className="hidden"
              playsInline
              autoPlay
              muted
              disablePictureInPicture
              disableRemotePlayback
            />
          )}

          {isPip ? (
            <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between bg-gradient-to-b from-black/55 to-transparent px-2 pb-6 pt-2">
              <p className="truncate text-[10px] font-medium text-white/90">
                {sharing
                  ? "Partage…"
                  : remoteReady
                    ? formatCallElapsed(elapsedSec)
                    : session.peerLabel}
              </p>
              <Maximize2 className="size-3.5 shrink-0 text-white/80" aria-hidden />
            </div>
          ) : null}
          {isPip ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                hangup();
              }}
              className="absolute bottom-1.5 left-1.5 z-[6] flex size-8 items-center justify-center rounded-full bg-red-500 shadow-md"
              aria-label="Raccrocher"
            >
              <PhoneOff className="size-3.5" />
            </button>
          ) : null}
          <audio ref={remoteAudioRef} autoPlay />
          {/* Pastilles multi-participants (caméras distantes) */}
          {remoteCams.length > 1 && !isPip ? (
            <div className="absolute bottom-3 left-1/2 z-[7] flex max-w-[90%] -translate-x-1/2 gap-2 overflow-x-auto pb-1">
              {remoteCams.map((cam) => (
                <div
                  key={cam.identity}
                  className="relative h-20 w-14 shrink-0 overflow-hidden rounded-lg border border-white/30 bg-black shadow-md md:h-24 md:w-16"
                  title={cam.name}
                >
                  <video
                    className="h-full w-full object-cover"
                    playsInline
                    autoPlay
                    muted
                    disablePictureInPicture
                    ref={(el) => {
                      if (el) {
                        remoteCamElsRef.current.set(cam.identity, el);
                        const t = remoteCamTracksRef.current.get(cam.identity);
                        if (t) t.attach(el);
                      } else {
                        remoteCamElsRef.current.delete(cam.identity);
                      }
                    }}
                  />
                  <span className="absolute inset-x-0 bottom-0 truncate bg-black/55 px-0.5 py-0.5 text-center text-[9px] text-white">
                    {cam.name}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center px-4 py-8 md:py-10">
          <div className="text-center">
            <div className="mx-auto mb-4 flex size-[4.5rem] items-center justify-center rounded-full bg-gradient-to-br from-white/20 to-white/5 text-2xl font-semibold ring-1 ring-white/15 shadow-[0_12px_40px_rgba(0,0,0,0.35)]">
              {session.peerLabel.slice(0, 1).toUpperCase()}
            </div>
            <p className="text-sm text-white/70">{statusLine}</p>
          </div>
          <audio ref={remoteAudioRef} autoPlay />
        </div>
      )}

      {!isPip ? (
        <div className="chat-call-dock relative z-10 flex shrink-0 flex-col items-center gap-2.5 px-3 pb-4 pt-1 md:px-4 md:pb-5">
          {showReactions ? (
            <div
              className="chat-call-reactions flex max-w-full items-center gap-1 overflow-x-auto px-2 py-1.5"
              role="listbox"
              aria-label="Réactionsions"
            >
              {CALL_REACTION_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  role="option"
                  className="flex size-10 shrink-0 items-center justify-center rounded-full text-xl transition hover:bg-white/15 active:scale-90"
                  onClick={() => {
                    sendReaction(emoji);
                    setShowReactions(false);
                  }}
                  aria-label={`Envoyer ${emoji}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          ) : null}
          {showInvite && canOpenInvite ? (
            <div className="chat-call-invite" role="listbox" aria-label="Inviter un participant">
              <div className="chat-call-invite-head">
                <p>Inviter · max 4</p>
              </div>
              {onInviteGuest ? (
                <div className="space-y-1.5 px-2 pb-2">
                  <input
                    type="email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    placeholder="Invité · e-mail"
                    className="w-full rounded-lg border border-white/15 bg-white/10 px-2 py-1.5 text-xs text-white outline-none placeholder:text-white/40"
                  />
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      className="flex-1 rounded-lg bg-white/15 px-2 py-1 text-[11px] font-medium text-white disabled:opacity-40"
                      disabled={inviteBusy}
                      onClick={() => {
                        void (async () => {
                          setInviteBusy(true);
                          setGuestHint(null);
                          const url = await onInviteGuest(guestEmail.trim());
                          setInviteBusy(false);
                          if (url) {
                            setGuestEmail("");
                            setGuestHint("Invitation envoyée");
                          }
                        })();
                      }}
                    >
                      Envoyer
                    </button>
                    {onCopyGuestLink ? (
                      <button
                        type="button"
                        className="flex-1 rounded-lg bg-white/15 px-2 py-1 text-[11px] font-medium text-white disabled:opacity-40"
                        disabled={inviteBusy}
                        onClick={() => {
                          void (async () => {
                            setInviteBusy(true);
                            setGuestHint(null);
                            const url = await onCopyGuestLink();
                            setInviteBusy(false);
                            setGuestHint(url ? "Lien copié" : "Impossible de copier");
                          })();
                        }}
                      >
                        Copier le lien
                      </button>
                    ) : null}
                  </div>
                  {guestHint ? <p className="text-[10px] text-white/70">{guestHint}</p> : null}
                </div>
              ) : null}
              <div className="chat-call-invite-list">
                {canInviteOthers && onInviteParticipant ? (
                  inviteCandidates.length === 0 ? (
                    <p className="chat-call-invite-empty">Aucun contact Suite</p>
                  ) : (
                    inviteCandidates.map((c) => {
                    const p = (c.presence || "offline").toLowerCase();
                    const busy = p === "incall";
                    const statusLabel =
                      p === "incall"
                        ? "En visio"
                        : p === "online"
                        ? "En ligne"
                        : p === "dnd"
                          ? "Ne pas déranger"
                          : p === "meeting"
                            ? "En réunion"
                            : "Hors ligne";
                    const dotClass =
                      p === "incall"
                        ? "chat-call-invite-dot chat-call-invite-dot--incall"
                        : p === "online"
                        ? "chat-call-invite-dot chat-call-invite-dot--online"
                        : p === "dnd"
                          ? "chat-call-invite-dot chat-call-invite-dot--dnd"
                          : p === "meeting"
                            ? "chat-call-invite-dot chat-call-invite-dot--meeting"
                            : "chat-call-invite-dot";
                    const sub =
                      (c.inGroup ? "Déjà dans le groupe" : null) ||
                      (c.clients && c.clients.length > 0
                        ? c.clients.slice(0, 2).join(" · ")
                        : null) ||
                      statusLabel;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        role="option"
                        className={`chat-call-invite-row ${busy ? "opacity-50" : ""}`}
                        disabled={inviteBusy || busy}
                        title={
                          busy
                            ? `${c.label} est déjà en visio`
                            : `Inviter ${c.label} (${statusLabel})`
                        }
                        onClick={() => {
                          void (async () => {
                            setInviteBusy(true);
                            try {
                              await onInviteParticipant?.(c.id);
                              setShowInvite(false);
                            } finally {
                              setInviteBusy(false);
                            }
                          })();
                        }}
                      >
                        <span className="chat-call-invite-avatar" aria-hidden>
                          {c.avatarUrl ? (
                            <img src={c.avatarUrl} alt="" />
                          ) : (
                            c.label.slice(0, 1).toUpperCase()
                          )}
                          <span className={dotClass} />
                        </span>
                        <span className="chat-call-invite-meta">
                          <span className="chat-call-invite-name">{c.label}</span>
                          <span className="chat-call-invite-sub">{sub}</span>
                        </span>
                        <UserPlus className="size-3.5 shrink-0 text-white/35" aria-hidden />
                      </button>
                    );
                  })
                  )
                ) : null}
              </div>
            </div>
          ) : null}
          <div className="chat-call-controls">
          <button
            type="button"
            onClick={() => void toggleMic()}
            className={`chat-call-ctrl ${!micOn ? "chat-call-ctrl--off" : ""}`}
            aria-label={micOn ? "Couper le micro" : "Activer le micro"}
            disabled={status !== "connected"}
          >
            {micOn ? <Mic className="size-5" /> : <MicOff className="size-5" />}
          </button>
          <button
            type="button"
            onClick={() => {
              setShowInvite(false);
              setShowReactions((v) => !v);
            }}
            className={`chat-call-ctrl ${showReactions ? "chat-call-ctrl--active" : ""}`}
            aria-label="Réactionsions emoji"
            aria-expanded={showReactions}
            disabled={status !== "connected"}
            title="Réactionsions"
          >
            <SmilePlus className="size-5" />
          </button>
          {canOpenInvite ? (
            <button
              type="button"
              onClick={() => {
                setShowReactions(false);
                setShowInvite((v) => !v);
              }}
              className={`chat-call-ctrl ${showInvite ? "chat-call-ctrl--active" : ""}`}
              aria-label="Inviter quelqu’un"
              title="Inviter (max 4)"
              disabled={status !== "connected"}
            >
              <UserPlus className="size-5" />
            </button>
          ) : null}
          {isVideo ? (
            <button
              type="button"
              onClick={() => void toggleCam()}
              className={`chat-call-ctrl ${!camOn ? "chat-call-ctrl--off" : ""}`}
              aria-label={camOn ? "Couper la caméra" : "Activer la caméra"}
              disabled={status !== "connected"}
            >
              {camOn ? <Video className="size-5" /> : <VideoOff className="size-5" />}
            </button>
          ) : null}
          {isVideo ? (
            <button
              type="button"
              onClick={() => void toggleScreenShare()}
              disabled={status !== "connected"}
              className={`chat-call-ctrl ${sharing ? "chat-call-ctrl--share" : ""}`}
              aria-label={sharing ? "Arrêter le partage d’écran" : "Partager l’écran"}
              title={sharing ? "Partage en cours — cliquer pour arrêter" : "Partage d’écran"}
            >
              {sharing ? <ScreenShareOff className="size-5" /> : <ScreenShare className="size-5" />}
            </button>
          ) : null}
          {isVideo && canFlipCam && camOn && status === "connected" ? (
            <button
              type="button"
              onClick={() => void flipCamera()}
              disabled={flipBusy}
              className="chat-call-ctrl"
              aria-label={
                facing === "user" ? "Passer à la caméra arrière" : "Passer à la caméra avant"
              }
              title="Inverser la caméra"
            >
              <SwitchCamera className={`size-5 ${flipBusy ? "animate-spin" : ""}`} />
            </button>
          ) : null}
          <button
            type="button"
            onClick={hangup}
            className="chat-call-ctrl chat-call-ctrl--hangup"
            aria-label="Raccrocher"
          >
            <PhoneOff className="size-6" />
          </button>
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <>
      {showCallBackdrop && !isPip ? (
        <div
          className={`chat-call-backdrop ${!isVideo ? "hidden md:block" : ""}`}
          aria-hidden
        />
      ) : null}
      {shell}
    </>
  );
}
